/**
 * US Phone / Fax formatting and validation utilities.
 *
 * Storage contract: canonical `XXX-XXX-XXXX` string (or null/empty).
 * Display contract: same format wherever a number is rendered.
 * Backend mirrors these rules in `/app/backend/utils/phone_format.py`
 * via Pydantic field validators on the TaxCollector model — DB always
 * stores normalized phone strings.
 */

/** Return only the digits from any input (handles null, numbers, strings). */
export const digitsOnly = (s) => String(s ?? '').replace(/\D+/g, '');

/**
 * Format US phone for display / live typing.
 *  - Empty / null / undefined → ''
 *  - Exactly 10 digits → '###-###-####'
 *  - Fewer digits → partial format ('302', '302-289', '302-289-6219')
 *  - More than 10 digits → truncated to 10 (prevents pasting 11+ digit blobs)
 */
export function formatUSPhone(input) {
  if (input === null || input === undefined || input === '') return '';
  const d = digitsOnly(input).slice(0, 10);
  if (d.length === 0) return '';
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
}

/** Display-only: 10-digit values → 123-456-7893; anything else is shown exactly as stored. */
export const formatUSPhoneDisplay = (s) => {
  if (s === null || s === undefined || s === '') return '';
  return digitsOnly(s).length === 10 ? formatUSPhone(s) : String(s);
};

/** Empty values are considered valid (use a separate "required" check). */
export const isValidUSPhone = (s) => {
  if (s == null || s === '') return true;
  return digitsOnly(s).length === 10;
};

/**
 * JCR-PHONE-DISPLAY-001 — display-only "(XXX) XXX-XXXX" for correction-request values.
 *  - 10 digits → (302) 395-5458; 11 digits with leading 1 → country code dropped
 *  - trailing extension ("x123", "ext 123", "ext. 123") preserved as " ext. 123"
 *  - anything else (incomplete / foreign / free text) is returned exactly as stored
 */
export const formatUSPhoneParens = (s) => {
  if (s === null || s === undefined || String(s).trim() === '') return '';
  const raw = String(s).trim();
  const m = raw.match(/^(.*?)(?:\s*(?:x|ext\.?|extension)\s*(\d{1,6}))?$/i);
  const base = m ? m[1] : raw;
  const ext = m && m[2] ? ` ext. ${m[2]}` : '';
  let d = digitsOnly(base);
  if (d.length === 11 && d.startsWith('1')) d = d.slice(1);
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}${ext}`;
};

/** Trim a string or pass through non-strings. */
export const trimStr = (s) => (typeof s === 'string' ? s.trim() : s);

/** Trim a string and return null if the result is empty. */
export const trimOrNull = (s) => {
  if (s == null) return null;
  if (typeof s !== 'string') return s;
  const t = s.trim();
  return t === '' ? null : t;
};
